import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import type { Promotion, PromotionCondition, PromotionDiscount } from '../queries/promotion.queries';

interface CreatePromotionInput {
  name: { en: string; th: string };
  description?: { en: string; th: string };
  type: 'discount' | 'bundle' | 'buy_x_get_y' | 'seasonal';
  conditions: PromotionCondition[];
  discount: PromotionDiscount;
  applicableCategories?: string[];
  applicableItems?: string[];
  applicableBranches?: string[];
  startDate: string;
  endDate: string;
  isActive: boolean;
  priority: number;
  autoApply: boolean;
  requiresCouponCode: boolean;
  couponCode?: string;
  usageLimit?: number;
}

// Create promotion
export function useCreatePromotion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePromotionInput) => {
      const response = await apiClient.post<APIResponse<{ promotion: Promotion }>>(
        '/promotions',
        data
      );
      return response.data.data.promotion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
    },
  });
}

// Update promotion
export function useUpdatePromotion(promotionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<CreatePromotionInput>) => {
      const response = await apiClient.patch<APIResponse<{ promotion: Promotion }>>(
        `/promotions/${promotionId}`,
        data
      );
      return response.data.data.promotion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', promotionId] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
    },
  });
}

// Delete promotion
export function useDeletePromotion(promotionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.delete(`/promotions/${promotionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
    },
  });
}

// Toggle promotion active status
export function useTogglePromotion(promotionId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (isActive: boolean) => {
      const response = await apiClient.patch<APIResponse<{ promotion: Promotion }>>(
        `/promotions/${promotionId}/toggle`,
        { isActive }
      );
      return response.data.data.promotion;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      queryClient.invalidateQueries({ queryKey: ['promotion', promotionId] });
      queryClient.invalidateQueries({ queryKey: ['active-promotions'] });
    },
  });
}

// Calculate eligible promotions for cart
export interface CartItem {
  menuItemId: string;
  categoryId?: string;
  quantity: number;
  unitPrice: number;
  size?: string;
}

export interface EligiblePromotion {
  promotion: {
    _id: string;
    name: { en: string; th: string };
    description?: { en: string; th: string };
    type: string;
    discount: any;
    autoApply: boolean;
    autoSuggest: boolean;
    priority: number;
    highlightBadge?: string;
    conditions?: any;
  };
  discountAmount: number;
  affectedItems: any[];
}

export interface CalculateEligibleResponse {
  eligiblePromotions: EligiblePromotion[];
  bestAutoApply?: EligiblePromotion;
  subtotal: number;
}

export function useCalculateEligiblePromotions() {
  return useMutation({
    mutationFn: async (data: {
      items: CartItem[];
      branchId: string;
      loyaltyMemberId?: string;
      customerId?: string;
    }) => {
      const response = await apiClient.post<APIResponse<CalculateEligibleResponse>>(
        '/promotions/calculate-eligible',
        data
      );
      return response.data.data;
    },
  });
}

export type { CreatePromotionInput };
